import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAboutData } from '../../Public/about-data';
import Image from '../../Utils/Image/image-component';
import NavLink from '../../Utils/NavLink/navlink-component';

/**
 * Members section of the About page (dark / purple public theme).
 * Reads `teamSection` from about-data; renders each member as a card that links
 * to its /team/:slug detail page. Centers gracefully with a single member.
 */
function AboutMembers({ asPage = false, teamSection: teamSectionProp }) {
  const { language = 'he', dir = 'rtl' } = useSelector((state) => state.systemData || {});
  const Heading = asPage ? 'h2' : 'h3';
  const aboutData = useMemo(() => getAboutData(language), [language]);

  // Prefer the S3-generated teamSection (passed in); fall back to the local seed.
  const teamSection = teamSectionProp || aboutData?.teamSection;
  const members = teamSection?.members || [];
  if (!members.length) return null;

  return (
    <section dir={dir} className="w-full bg-primary-950 py-16 md:py-20 px-4">
      <div className="max-w-[1100px] mx-auto text-center">
{/* 
        {teamSection.eyebrow && (
          <span className="text-secondary-500 text-sm md:text-base font-bold uppercase">
            {teamSection.eyebrow}
          </span>
        )} */}

        <Heading className="font-secondary font-extralight text-white uppercase leading-none mt-3 text-4xl md:text-5xl lg:text-6xl">
          {teamSection.title}
        </Heading>

        {teamSection.description && (
          <p className="mt-4 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {teamSection.description}
          </p>
        )}

        <div className="mt-12 flex flex-wrap items-start justify-center gap-10 md:gap-16">
          {members.map((member) => (
            <NavLink
              key={member.slug}
              href={`/team/${member.slug}`}
              title={member.name}
              showUnderline={false}
              preserveStyle
              className="flex flex-col items-center text-center group !opacity-100"
            >
              <div className="relative mx-auto w-[240px] h-[240px] md:w-[280px] md:h-[280px] rounded-full overflow-hidden ring-2 ring-secondary-500/40 shadow-2xl shadow-black/60 transition-transform duration-300 group-hover:scale-[1.03]">
                <Image
                  src={member.img}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  wrapperClassName="w-full h-full"
                  useSekeleton={false}
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/50 to-transparent" />
              </div>

              <h3 className="mt-5 text-3xl md:text-4xl font-bold text-white leading-none group-hover:text-secondary-500 transition-colors duration-200">
                {member.name}
              </h3>

              <h4 className="mt-1.5 text-base md:text-lg font-bold text-secondary-500 leading-tight">
                {member.role}
              </h4>

              <p className="mt-3 text-sm md:text-base text-white/60 leading-relaxed max-w-[320px]">
                {member.description}
              </p>
            </NavLink>
          ))}
        </div>
      </div>
    </section>
  );
}

export default AboutMembers;
